// frontend/src/pages/Profile.js
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { fetchNuclearPrinciples, fetchQContexts } from '../api';
import { AuthContext } from '../context/AuthContext';
import RatingSphere3D from '../components/RatingSphere3D';
import ProfileBackground from '../components/ProfileBackground';
import './Profile.css';
import { useAppBackground } from '../context/AppBackgroundContext';
import ReadableText from '../components/ReadableText';
import { useI18n } from '../i18n';

const extractSafeText = (data) => {
  if (!data) return '';

  let current = data;

  // Desempaquetar las capas de objetos anidados
  while (typeof current === 'object' && current !== null) {
    if (current.summary) {
      current = current.summary;
    } else if (current.text) {
      current = current.text;
    } else {
      // Si llegamos a un objeto que no tiene .summary ni .text, lo forzamos a string
      return JSON.stringify(current);
    }
  }

  // Para este punto, 'current' DEBE ser un primitivo (string, number, etc.)
  if (typeof current === 'string') return current;
  return String(current);
};

function Profile() {
  const { t } = useI18n();
  const { user, refreshUser } = useContext(AuthContext);
  const [summary, setSummary] = useState(extractSafeText(user?.profileSummary || user?.summary));
  const [principles, setPrinciples] = useState([]);
  const [qContexts, setQContexts] = useState([]);
  const [rating, setRating] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const [bgImage, setBgImage] = useState(null);
  const [isPrinciplesOpen, setIsPrinciplesOpen] = useState(false);
  const [isContextsOpen, setIsContextsOpen] = useState(false);
  const [dreamPage, setDreamPage] = useState(0);
  const [contextPage, setContextPage] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [inventoryItems, setInventoryItems] = useState([]);
  const navigate = useNavigate();
  const { refreshBackgrounds } = useAppBackground() || {};

  const defaultSummary = t('profile.defaultSummary');

  const qCount = user?.qConnections || 0;

  // Determinar Rango Evolutivo
  let brainRank = t('profile.ranks.brainstem');
  if (qCount >= 9999) brainRank = t('profile.ranks.illuminated');
  else if (qCount >= 7000) brainRank = t('profile.ranks.frontal');
  else if (qCount >= 3000) brainRank = t('profile.ranks.neocortex');
  else if (qCount >= 1000) brainRank = t('profile.ranks.limbic');

  // Determinar Títulos Obtenidos (Acumulativos)
  const earnedTitles = [];
  if (qCount >= 1) earnedTitles.push(t('profile.titles.explorer'));
  if (qCount >= 2000) earnedTitles.push(t('profile.titles.miner'));
  if (qCount >= 5000) earnedTitles.push(t('profile.titles.archaeologist'));
  if (qCount >= 7777) earnedTitles.push(t('profile.titles.salmon'));
  if (qCount >= 9999) earnedTitles.push(t('profile.titles.meditator'));

  useEffect(() => {
    const fetchProfileSummary = async () => {
      try {
        const res = await api.get('/profile/summary');
        const payload = res.data;
        const resolved = extractSafeText(payload?.summary || payload?.text);
        setSummary(resolved);
      } catch (err) {
        console.error('Error fetching profile summary:', err);
        setSummary(defaultSummary);
      }
    };

    const fetchBackground = async () => {
      try {
        const res = await api.get('/profile/images/active');
        if (res.data.image) {
          setBgImage(res.data.image.imageData);
          refreshBackgrounds?.();
          api.post('/profile/images/ensure-daily').then(dailyRes => {
             if (dailyRes.data.created && dailyRes.data.image) {
                 setBgImage(dailyRes.data.image.imageData);
                 refreshBackgrounds?.();
             }
          }).catch(e => console.warn('Daily generation check failed:', e));
        } else {
          const genRes = await api.post('/profile/images/ensure-daily');
           if (genRes.data.image) {
               setBgImage(genRes.data.image.imageData);
               refreshBackgrounds?.();
           }
        }
      } catch (e) {
        console.error('Error fetching background:', e);
      }
    };

    const loadNuclearPrinciples = async () => {
      try {
        const res = await fetchNuclearPrinciples();
        if (res.data && res.data.ok) {
          setPrinciples(res.data.principles || []);
        }
      } catch (err) {
        console.error('Error fetching nuclear principles:', err);
      }
    };

    const loadQContexts = async () => {
      try {
        const res = await fetchQContexts();
        if (res.data && res.data.ok) {
          setQContexts(res.data.contexts || []);
        }
      } catch (err) {
        console.error('Error fetching Q-Contexts:', err);
      }
    };

    const loadInventory = async () => {
      try {
        const res = await api.get('/users/inventory');
        if (res.data && res.data.items) {
          setInventoryItems(res.data.items);
        }
      } catch (err) {
        console.error('Error fetching inventory:', err);
      }
    };

    if (user) {
      if (refreshUser) refreshUser();
      fetchProfileSummary();
      fetchBackground();
      loadNuclearPrinciples();
      loadQContexts();
      loadInventory();
    }
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const itemsPerView = windowWidth <= 768 ? 1 : 2;

  const handleRatingSelect = (value) => {
    setRating(value);
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    try {
      const fullFeedback = { feedback, type: feedbackType, rating };
      await api.post('/profile/feedback', { feedback: JSON.stringify(fullFeedback) });
      setSubmitStatus(t('profile.feedbackSent'));
      setFeedback('');
      setFeedbackType('');
    } catch (error) {
      setSubmitStatus(t('profile.feedbackError'));
    }
  };

  const nextDream = () => {
    if (dreamPage < principles.length - itemsPerView) {
      setDreamPage(prev => prev + 1);
    }
  };
  const prevDream = () => {
    if (dreamPage > 0) {
      setDreamPage(prev => prev - 1);
    }
  };

  const nextContext = () => {
    if (contextPage < qContexts.length - itemsPerView) {
      setContextPage(prev => prev + 1);
    }
  };
  const prevContext = () => {
    if (contextPage > 0) {
      setContextPage(prev => prev - 1);
    }
  };

  if (!user) {
    return <p>{t('profile.loginRequired')}</p>;
  }

  const isUrl = (str) => {
    if (typeof str !== 'string') return false;
    return /^(https?:\/\/|blob:)/i.test(str.trim());
  };

  return (
    <>
      <ProfileBackground imageData={bgImage} />
      <div id="profile-main" className="profile-container" style={{ position: 'relative', zIndex: 10, pointerEvents: 'none' }}>
        <div className="profile-header" style={{ pointerEvents: 'auto' }}>
          <div className="profile-info-column">
            {isUrl(user.avatar || user.avatar_url) ? (
              <img src={(user.avatar || user.avatar_url).trim()} alt={t('profile.avatarAlt')} className="avatar-large-img" referrerPolicy="no-referrer" />
            ) : (
              <div className="avatar-large">{user.avatar || user.avatar_url || '🙂'}</div>
            )}
            <h2 className="profile-username">{t('profile.hello', { name: user.displayAlias || user.displayName || user.username })}</h2>
          </div>
          <div className="profile-actions-column">
            {/* Audio controls removed */}
          </div>
        </div>

        <div id="profile-summary" className="profile-summary" style={{ pointerEvents: 'auto' }}>
          <p><ReadableText text={extractSafeText(summary) || defaultSummary} label={t('profile.readSummary')}>{extractSafeText(summary) || defaultSummary}</ReadableText></p>
        </div>

        {/* --- SECCIÓN DE PRINCIPIOS NUCLEARES --- */}
        <div className="nuclear-principles-section" style={{ pointerEvents: 'auto' }}>
          <button
            className="collapsible-header"
            type="button"
            onClick={() => setIsPrinciplesOpen(!isPrinciplesOpen)}
            aria-expanded={isPrinciplesOpen}
            aria-controls="profile-principles-carousel"
          >
            <h3 className="principles-title">
              {t('profile.nucleiTitle')} <span className="principles-subtitle">- {t('profile.nucleiSubtitle')}</span>
            </h3>
            <span className="toggle-icon">{isPrinciplesOpen ? '↓' : '→'}</span>
          </button>

          {isPrinciplesOpen && (
            <div id="profile-principles-carousel" className="carousel-main-wrapper">
              {principles.length > 0 ? (
                <>
                  <button
                    className={`nav-arrow left ${dreamPage === 0 ? 'hidden' : ''}`}
                    onClick={prevDream}
                  >
                    ‹
                  </button>
                  <div className="carousel-viewport">
                    <div
                      className="carousel-track"
                      style={{
                        transform: `translateX(-${dreamPage * (100 / itemsPerView)}%)`
                      }}
                    >
                      {principles.map((p, index) => (
                        <div key={p.id || index} className="carousel-item">
                          <div className="principle-card dream-card">
                            <div className="principle-glow"></div>
                            <span className="principle-date">
                              {new Date(p.createdAt).toLocaleDateString()}
                            </span>
                            <div className="scrollable-card-text">
                              <p className="principle-content"><span aria-hidden="true">"</span><ReadableText text={p.content} label={t('profile.readNucleus')}>{p.content}</ReadableText><span aria-hidden="true">"</span></p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    className={`nav-arrow right ${dreamPage >= principles.length - itemsPerView ? 'hidden' : ''}`}
                    onClick={nextDream}
                  >
                    ›
                  </button>
                </>
              ) : (
                <p className="fallback-msg">{t('profile.forming')}</p>
              )}
            </div>
          )}
        </div>

        {/* --- SECCIÓN DE CONTEXTOS Q --- */}
        <div className="q-contexts-section" style={{ pointerEvents: 'auto' }}>
          <button
            className="collapsible-header"
            type="button"
            onClick={() => setIsContextsOpen(!isContextsOpen)}
            aria-expanded={isContextsOpen}
            aria-controls="profile-contexts-carousel"
          >
            <h3 className="principles-title">
              {t('profile.portalFiles')} <span className="principles-subtitle">- {t('profile.portalFilesSubtitle')}</span>
            </h3>
            <span className="toggle-icon">{isContextsOpen ? '↓' : '→'}</span>
          </button>

          {isContextsOpen && (
            <div id="profile-contexts-carousel" className="carousel-main-wrapper">
              {qContexts.length > 0 ? (
                <>
                  <button
                    className={`nav-arrow left ${contextPage === 0 ? 'hidden' : ''}`}
                    onClick={prevContext}
                  >
                    ‹
                  </button>
                  <div className="carousel-viewport">
                    <div
                      className="carousel-track"
                      style={{
                        transform: `translateX(-${contextPage * (100 / itemsPerView)}%)`
                      }}
                    >
                      {qContexts.map((ctx) => {
                        const data = ctx.meta?.q_context;
                        if (!data) return null;
                        return (
                          <div key={ctx.id} className="carousel-item">
                            <div className="q-context-card context-card">
                              <div className="q-context-header">
                                <span className="principle-date">
                                  {new Date(ctx.createdAt).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="scrollable-card-text">
                                <p className="q-context-explanation"><ReadableText text={data.explanation} label={t('profile.readPortalAnalysis')}>{data.explanation}</ReadableText></p>
                              </div>
                              {data.videoId && (
                                <div className="q-context-video">
                                  <iframe
                                    width="100%"
                                    height="100%"
                                    src={`https://www.youtube.com/embed/${data.videoId}`}
                                    title="YouTube video player"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                  ></iframe>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <button
                    className={`nav-arrow right ${contextPage >= qContexts.length - itemsPerView ? 'hidden' : ''}`}
                    onClick={nextContext}
                  >
                    ›
                  </button>
                </>
              ) : (
                <p className="fallback-msg">{t('profile.forming')}</p>
              )}
            </div>
          )}
        </div>

        {/* --- RPG Dashboard Section --- */}
        <section className="rpg-dashboard-unified full-width-section" style={{ pointerEvents: 'auto' }}>
          <div className="achievements-heading-block">
            <h3 className="achievements-title">Archivos de Portal & Estado Evolutivo</h3>
            <small className="ai-subtitle">Consolidado de métricas y hallazgos en el ecosistema de Qproject</small>
          </div>

          <div className="dashboard-section-title">A. Estado General</div>
          <div className="rpg-stats-grid">
            <div className="stat-box">
              <span className="stat-label">⚡ {t('settings.status', 'Estado')}</span>
              <span className="stat-value">{t('common.active', 'Activo')}</span>
            </div>
            <div className="stat-box">
              <span className="stat-label">📜 {t('common.questionnaire', 'Cuestionario')}</span>
              <span className="stat-value">{user?.questionnaireCompleted ? t('common.yes', 'Sí') : t('common.no', 'No')}</span>
            </div>
            <div className="stat-box">
              <span className="stat-label">⏳ {t('settings.daysActive', 'Días Activo')}</span>
              <span className="stat-value">∞</span>
            </div>
            <div className="stat-box">
              <span className="stat-label">💠 {t('settings.level', 'Nivel')}</span>
              <span className="stat-value">1</span>
            </div>
          </div>

          <div className="dashboard-section-title">B. Métricas Neuro-Simbióticas</div>
          <div className="achievement-stats rpg-stats-grid">
            <div className="stat-box">
              <span className="stat-label">🔗 {t('profile.qConnections')}</span>
              <span className="stat-value">{qCount}</span>
            </div>
            <div className="stat-box">
              <span className="stat-label">🧠 {t('profile.evolutionState')}</span>
              <span className="stat-value rank-highlight">{brainRank}</span>
            </div>
          </div>

          <div className="titles-container" style={{ marginBottom: '1.5rem' }}>
            <h4 className="titles-heading">{t('profile.unlockedTitles')}</h4>
            <ul className="titles-list">
              {earnedTitles.map((title, index) => (
                <li key={index} className="title-item">✦ {title}</li>
              ))}
            </ul>
          </div>

          <div className="dashboard-section-title">🎒 Digital Inventory / Caché de Artefactos</div>
          <div className="inventory-section">
            <div className="inventory-grid">
              {Array.from({ length: Math.max(8, inventoryItems.length) }).map((_, index) => {
                const item = inventoryItems[index];
                if (item) {
                  return (
                    <div key={item.id} className="inventory-card acquired">
                      <div className="inventory-item-header">
                        <span className="inventory-icon g-fill-core">✦</span>
                        <span className="inventory-item-type">{item.item_type}</span>
                      </div>
                      <div className="inventory-item-body">
                        <h5 className="inventory-item-key">{item.item_key}</h5>
                        {item.metadata && (
                          <div className="inventory-item-meta">
                            {Object.entries(item.metadata).map(([k, v]) => (
                              <span key={k} className="meta-pill">{k}: {String(v)}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div key={`empty-${index}`} className="inventory-card empty-slot">
                      <div className="empty-slot-icon">?</div>
                    </div>
                  );
                }
              })}
            </div>
          </div>
        </section>

        {/* --- Feedback Section --- */}
        <section className="profile-feedback-frame full-width-section" style={{ pointerEvents: 'auto', marginTop: '2rem' }}>
          <div className="profile-feedback-heading">
            <div className="mini-sphere">
              <RatingSphere3D onSelect={handleRatingSelect} radius={1.2} />
            </div>
            <div>
              <span className="profile-rating-question">{t('profile.feedbackQuestion')}</span>
              <h3>{t('profile.refineTitle')}</h3>
              <p>{t('profile.refineBody')}</p>
            </div>
          </div>
          <form onSubmit={handleFeedbackSubmit} className="profile-feedback-form">
            <textarea
              id="profile-feedback-input"
              placeholder={t('profile.feedbackPlaceholder')}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
            />
            <div className="profile-feedback-actions">
              <div className="profile-feedback-focus-group" role="group" aria-label={t('profile.focusPlaceholder')}>
                {[
                  { value: 'reflexion', label: t('profile.focusReflection') },
                  { value: 'autoanalisis', label: t('profile.focusAnalysis') },
                  { value: 'revision', label: t('profile.focusGoals') },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`profile-focus-pill ${feedbackType === option.value ? 'active' : ''}`}
                    onClick={() => setFeedbackType(option.value)}
                    aria-pressed={feedbackType === option.value}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <button id="btn-profile-submit" type="submit">{t('profile.sendFeedback')}</button>
            </div>
          </form>
          {submitStatus && <p className="submit-status">{submitStatus}</p>}
        </section>

        <button className="gallery-access-btn" onClick={() => navigate('/profile/gallery')} title={t('profile.galleryTitle')}>+</button>
      </div>
    </>
  );
}

export default Profile;
